/**
 * IconSearchInput Component
 *
 * Search input for filtering icons in the IconPicker.
 * Includes screen reader announcements for search results.
 *
 * Features:
 * - Search icon visual indicator
 * - Clear button when search has value
 * - Screen reader announcements for result count
 * - Loading indicator during search filtering
 */

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from '@/utils/classname';

interface IconSearchInputProps {
  /** Current search term */
  value: string;
  /** Callback when search term changes */
  onChange: (value: string) => void;
  /** Number of icons matching current search */
  resultCount: number;
  /** Whether filtering is currently in progress */
  isFiltering: boolean;
}

/**
 * Search input with accessibility announcements.
 *
 * Uses aria-live for announcing search results to screen readers.
 *
 * @example
 * ```tsx
 * <IconSearchInput
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   resultCount={filteredIcons.length}
 *   isFiltering={isFiltering}
 * />
 * ```
 */
export function IconSearchInput({
  value,
  onChange,
  resultCount,
  isFiltering,
}: IconSearchInputProps) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="text-chariot-text-tertiary pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
      <input
        type="text"
        role="searchbox"
        placeholder="Search icons..."
        value={value}
        onChange={e => onChange(e.target.value)}
        className={cn(
          'w-full rounded-lg py-2 pr-8 pl-9',
          'border-chariot-stroke bg-chariot-background-secondary border',
          'text-chariot-text-primary placeholder:text-chariot-text-tertiary',
          'focus:ring-chariot-stroke-focused focus:ring-2 focus:outline-hidden'
        )}
        aria-describedby="search-results-count"
        data-testid="icon-search-input"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="hover:bg-chariot-background-tertiary absolute top-1/2 right-2 -translate-y-1/2 rounded p-1"
          aria-label="Clear search"
          data-testid="icon-search-clear"
        >
          <XMarkIcon className="text-chariot-text-tertiary size-4" />
        </button>
      )}
      {/* Screen reader announcement */}
      <div id="search-results-count" aria-live="polite" className="sr-only">
        {isFiltering
          ? 'Searching...'
          : `${resultCount} icons found${value ? ` for "${value}"` : ''}`}
      </div>
    </div>
  );
}
